import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Separator } from "../../components/ui/separator";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatCurrencyINR } from "../../lib/enhanced-currency-utils";
import {
  FileText,
  Trash2,
  Download,
  Package,
  Users,
  Receipt,
  TrendingUp,
  Upload,
  Eye,
  ClipboardList,
  Layers,
} from "lucide-react";
import { POPreviewModal } from "../../components/purchaseOrder/POPreviewModal";
import { InvoiceDetailButton } from "../../components/purchaseOrder/InvoiceDetailDialog";
import { POStatusBadge } from "../../components/purchaseOrder/POStatusBadge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { AssetDetailSidebar } from "../../components/purchaseOrder/AssetDetailSidebar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import apiService from "../../services/apiService";
import { enrichAssetsWithShipmentInsurance } from "../../lib/assetInsurance";
import { toast } from "react-toastify";
import PoEdit from "../../components/purchaseOrder/PoEdit";
import { Switch } from "../../components/ui/switch";
import React from "react";
import { AssetTypeIcon } from "../../lib/asset-types";
import { useCustomerWorkflow } from "../../context/CustomerWorkflowContext";

interface AvailableAssets {
  asset_type: string;
  attachments: string[];
  cgst: number;
  delivery_address: string;
  delivery_proof_url: string;
  description: string;
  e_bay_bill_url: string;
  gst_treatment: string;
  hsn: string;
  id: number;
  igst: number;
  is_bulk: boolean;
  item: string;
  last_updated: string;
  loading_receipt_url: string;
  notes: string;
  purchase_order_id: number;
  qty: number;
  remaining_qty: number;
  sgst: number;
  shipped_from: string;
  shipping_company: string;
  status: string;
  status_history: any[];
  tax_rate: number;
  tracking_id: string;
  unit_price: number;
  utgst: number;
}

interface PODetailProps {
  poDetails: any;
  onClose?: () => void;
  adminUserList: adminUserList[];
  indiaState: any[];
}

interface adminUserList {
  name: string;
  id: number;
}

interface AssetTracking {
  asset_category: string;
  asset_insurance_url: string;
  asset_invoice_url: string;
  asset_line_id: string;
  asset_type: string;
  attachments: string[];
  cgst: number;
  created_at: string;
  delivery_address: string;
  delivery_proof_url: string;
  description: string;
  e_bay_bill_url: string;
  gst_treatment: string;
  hsn: string;
  id: number;
  igst: number;
  insurance_end_date: string;
  insurance_provider: string;
  insurance_start_date: string;
  invoice_id: number;
  is_bulk: boolean;
  is_rental_schedule_tagged: boolean;
  item: string;
  last_updated: string;
  loading_receipt_url: string;
  make_brand: string;
  model_no: string;
  notes: string;
  po_id: number;
  primaryImageUrl: string;
  qty: number;
  serial_number: string;
  sgst: number;
  shipped_from: string;
  shipping_company: string;
  specification: string;
  status: string;
  statusHistory: any[];
  tax_rate: number;
  tracking_id: string;
  transit_insurance_url: string;
  unique_asset_id: string;
  unit_price: number;
  updated_at: string;
  updated_by: number;
  utgst: number;
  warranty_end_date: string;
  warranty_provider: string;
  warranty_start_date: string;
}

export default function PODetail({
  poDetails,
  onClose,
  adminUserList,
  indiaState,
}: PODetailProps) {
  let po = poDetails;
 
  const [poPreviewOpen, setPoPreviewOpen] = useState(false);
  const [supplier, setSupplier] = useState<any>();
  const [customer, setCustomer] = useState<any>();
  const [invs, setInvs] = useState<any[]>([]);
  const [assetTracking, setAssetTracking] = useState<AssetTracking[]>([]);
  const [assetLines, setAssetLines] = useState<any[]>(poDetails.lines);
  const [assetsCategories, setAssetsCategories] = useState<any[]>([]);
  const [paidPayments, setPaidPayments] = useState<any[]>([]);
  const [currentStatus, setCurrentStatus] = useState<string>(poDetails.status);
  const [attachmentUrl, setAttachmentUrl] = useState<string>(
    poDetails.attachmentUrl || ""
  );
  const [uploadingDocType, setUploadingDocType] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const [assetOpen, setAssetOpen] = useState(false);
  const totals = useMemo(() => computePODetailTotals(assetLines), [assetLines]);

  const handlePrimaryImageChange = useCallback(
    (assetId: string, imageUrl: string) => {
      setAssetTracking((prev) =>
        prev.map((asset) =>
          asset.id === Number(assetId) ? { ...asset, primaryImageUrl: imageUrl } : asset
        )
      );
      setSelectedAsset((prev: any) =>
        prev?.id === assetId ? { ...prev, primaryImageUrl: imageUrl } : prev
      );
    },
    []
  );

  useEffect(() => {
    fetchInvoices();
    fetchAssetsCategories();
  }, []);

  const fetchInvoices = async () => {
    const response = await apiService.get<any>(
      `/purchase-order/get-invoices/${po.id}`
    );
    setInvs(response.data);
  };

  const fetchAssetsCategories = async () => {
    const response = await apiService.get<any>(
      `/purchase-order/get-asset-categories-list`
    );
    setAssetsCategories(response.data);
  };

  if (!po) return <div>PO not found</div>;

  const approve = async () => {
    try {
      await apiService.patch<any>(`/purchase-order/${po.id}`, {
        status: "approved",
      });
      toast.success("PO approved successfully");
      setCurrentStatus("approved");
      po.approvedBy = getCurrentUserId();
    } catch (error) {
      toast.error("Failed to approve PO");
    }
  };

  const getCurrentUserId = () => {
    try {
      const userDetails = localStorage.getItem("detail");
      if (!userDetails) return null;

      const userData = JSON.parse(userDetails);
      return userData?.userId?.toString() || null;
    } catch (error) {
      console.error("Error parsing user details:", error);
      return null;
    }
  };

  const markReview = async () => {
    try {
      await apiService.patch<any>(`/purchase-order/${po.id}`, {
        status: "review",
      });
      toast.success("PO submitted for review");
      setCurrentStatus("review");
    } catch (error) {
      toast.error("Failed to submit PO for review");
    }
  };

  useEffect(() => {
    fetchSuppliers();
    fetchCustomer();
    fetchAssetsTracking();
    fetchPayments();
  }, []);

  const fetchAssetsTracking = async () => {
    const response = await apiService.get<any>(
      `/purchase-order/get-assets-tracking/${po.id}`
    );
    const assets: AssetTracking[] = Array.isArray(response.data) ? response.data : [];
    const withInsurance = await enrichAssetsWithShipmentInsurance(assets, {
      customerId: po.customerId,
      noaId: po.noa_id ?? po.noaId,
    });
    setAssetTracking(withInsurance);
  };

  const fetchSuppliers = async () => {
    if (po.supplierId) {
      const response = await apiService.get<any>(`/supplier/get-supplier-by-id/${po.supplierId}`);
      setSupplier(response.supplier);
    }
  };
  const fetchCustomer = async () => {
    if (po.customerId) {
      const response = await apiService.get<any>(
        `/purchase-order/customer-name/${po.customerId}`
      );
      setCustomer(response.data);
    }
  };

  const fetchPayments = async () => {
    if (po.id) {
      const response = await apiService.get<any>(
        `/purchase-order/get-payments-by-po/${po.id}`
      );
      setPaidPayments(response.data);
    }
  };

  const fetchPoAssetLines = async () => {
    if (po.id) {
      const response = await apiService.get<any>(
        `/purchase-order/po-asset-line/${po.id}`
      );
      setAssetLines(response.data);
    }
  };

  const handleRefresh = async () => {
    fetchInvoices();
    fetchPayments();
    fetchAssetsTracking();
  };

  const totalQty = useMemo(
    () => assetLines.reduce((sum, line) => sum + (line.qty || 0), 0),
    [assetLines]
  );

  const totalQtyDisplay = useMemo(() => {
    const rounded = Math.round(totalQty * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
  }, [totalQty]);

  // Hero stats
  const heroStats = {
    totalAssets: totalQtyDisplay,
    totalValue: totals.total,
    invoicesCount: invs.length,
    deliveredAssets: assetTracking.filter((l: any) => l.status === "delivered")
      .length,
  };

  const handleUpdateAssetDetails = (asset: any) => {
    setAssetTracking((prev: any[]) =>
      prev.map((a) => (a.id === asset.id ? { ...a, ...asset } : a))
    );
    if (selectedAsset?.id === asset.id) {
      setSelectedAsset((prev: any) => (prev ? { ...prev, ...asset } : prev));
    }
  };

  const handleUpdateAssetStatus = async (id: string, data: any) => {
    try {
      const response: any = await apiService.post(
        "/purchase-order/update-asset-tracking-status",
        { ...data, id }
      );
      toast.success("Updated Successfully");
    } catch {
      toast.error("Failed to Update");
    }

    await fetchAssetsTracking();
  };

  const supplierSpoc = supplier?.spocDetails?.find(
    (s: any) => String(s.id) === String(po.supplierSpocId)
  );

  const handleViewAttachment = (url: string) => window.open(url, "_blank");

  const handleRemoveAttachment = async (type: string) => {
    if (!attachmentUrl) return;

    try {
      // call backend to remove attachment for this invoice
      // adjust endpoint/path if your API differs

      await apiService.post(`/purchase-order/delete-po-attachment/${po.id}`, {
        type: type,
      });

      setAttachmentUrl("");

      // update local UI

      toast.success("Attachment removed successfully");

      // // refresh related data so parent UI reflects change
      // if (typeof addPayment === "function") addPayment();
      // if (typeof fetchInvoicesAssets === "function") await fetchInvoicesAssets();
    } catch (err) {
      console.error("Failed to remove attachment", err);
      toast.error("Failed to remove attachment");
    }
  };

  const handleUploadForType = (type: string) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.doc,.docx,image/*";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      // setUploadingDocType(docType);
      try {
        const fd = new FormData();
        fd.append("files", file);
        fd.append("customerId", po.customerId);
        fd.append("folder", po.customerId + "/po/invoice/attachment");
        fd.append("type", String(type || ""));

        const uploadRes: any = await apiService.post(
          `/purchase-order/po-doc-upload/${po.id}`,
          fd,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        const urls = uploadRes.data?.urls ?? uploadRes.data ?? [];
        // take first url (adjust if backend returns single string)
        setAttachmentUrl(urls[0]);
        toast.success("File Upload Successfully");
      } catch (err) {
        toast.error("File Upload Failed");
        console.error("Upload failed", err);
      } finally {
        setUploadingDocType(null);
      }
    };
    input.click();
  };

  const handleAssetViewDetails = (asset: any) => {
    setSelectedAsset(asset);
    setAssetOpen(true);
  };

  return (
    <>
      {/* <SEO
        title={`PO ${po.id} • Detail`}
        description={`PO for ${customer?.name || "Customer"} with ${supplier?.name || "Supplier"}.`}
        canonical={`/pos/${po.id}`}
      /> */}

      {/* Hero Header */}
      <div className="mb-8 bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-lg p-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          {/* Left: Title + Status */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              {onClose && (
                <Button variant="ghost" size="sm" onClick={onClose}>
                  ← Back
                </Button>
              )}
              <h1 className="text-3xl font-bold text-foreground">
                {po.purchaseOrderCode}
              </h1>
              <POStatusBadge status={currentStatus} />
            </div>
          </div>

          {/* Right: Action Buttons */}
          <div className="flex justify-end gap-2 flex-wrap ml-auto">
            {currentStatus === "draft" && (
              <>
                <PoEdit
                  po={po}
                  suppliers={supplier}
                  onCreate={fetchPoAssetLines}
                  deliveryAddress={po.deliveryAddress}
                  indiaState={indiaState}
                />
                <Button variant="secondary" onClick={markReview}>
                  Submit for Review
                </Button>
              </>
            )}
            {currentStatus === "review" && (
              <ConfirmApprove onConfirm={approve} />
            )}
            <Button
              variant="outline"
              onClick={() => setPoPreviewOpen(true)}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Download PO
            </Button>
          </div>
        </div>

        {/* Enhanced Hero Stats */}
        <div className="mt-4 flex justify-end">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center w-auto">
            <div className="bg-gradient-to-br from-secondary/5 to-secondary/10 border border-secondary/20 rounded-lg p-3 hover:shadow-md transition-all duration-200 w-28 md:w-32">
              <div className="text-lg font-semibold text-[hsl(var(--color-admin-secondary))] mb-1">
                {heroStats.totalAssets}
              </div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Total Assets
              </div>
            </div>

            <div className="bg-gradient-to-br from-secondary/5 to-secondary/10 border border-secondary/20 rounded-lg p-3 hover:shadow-md transition-all duration-200 w-28 md:w-32">
              <div className="text-lg font-semibold text-[hsl(var(--color-admin-primary))] mb-1">
                ₹{(heroStats.totalValue / 100000).toFixed(1)}L
              </div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Total Value
              </div>
            </div>

            <div className="bg-gradient-to-br from-secondary/5 to-secondary/10 border border-secondary/20 rounded-lg p-3 hover:shadow-md transition-all duration-200 w-28 md:w-32">
              <div className="text-lg font-semibold text-[hsl(var(--color-admin-secondary))] mb-1">
                {heroStats.invoicesCount}
              </div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Invoices
              </div>
            </div>

            <div className="bg-gradient-to-br from-secondary/5 to-secondary/10 border border-secondary/20 rounded-lg p-3 hover:shadow-md transition-all duration-200 w-28 md:w-32">
              <div className="text-lg font-semibold text-[hsl(var(--color-admin-secondary))] mb-1">
                {heroStats.deliveredAssets}
              </div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Delivered
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="assets" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 bg-muted/50">
          <TabsTrigger value="assets" className="gap-2">
            <Package className="h-4 w-4" />
            Assets & Details
          </TabsTrigger>
          <TabsTrigger value="invoices" className="gap-2">
            <Receipt className="h-4 w-4" />
            Proforma Invoice
          </TabsTrigger>
          <TabsTrigger value="tracking" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            Asset Tracking
          </TabsTrigger>
        </TabsList>

        {/* Assets & Details Tab */}
        <TabsContent value="assets" className="mt-0 space-y-6">
          <div className="grid gap-6 lg:grid-cols-3 items-start">
            {/* Asset Lines */}
            <Card className="lg:col-span-2 flex flex-col max-h-[calc(100vh-240px+1cm)] min-h-0">
              <CardHeader className="shrink-0">
                <CardTitle>Asset Lines</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 min-h-0 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-white text-left text-muted-foreground shadow-[0_1px_0_0_hsl(var(--border))]">
                    <tr className="border-b bg-white">
                      {/* <th className="py-2 pr-4">Item</th> */}
                      <th className="py-2 pr-4 bg-white">Item</th>
                      <th className="py-2 pr-4 bg-white">Description</th>
                      <th className="py-2 pr-4 bg-white">HSN</th>
                      <th className="py-2 pr-4 bg-white">Unit Price</th>
                      <th className="py-2 pr-4 bg-white">Qty</th>
                      <th className="py-2 pr-4 bg-white">Tax Rate</th>
                      <th className="py-2 pr-0 text-right bg-white">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assetLines.map((l) => {
                      const {
                        cgst,
                        sgst,
                        igst,
                        utgst,
                        total,
                      } = getPoLineAmounts(l);
                      const isInterState = igst > 0;

                      return (
                        <tr
                          key={l.id}
                          className="border-b last:border-0 hover:bg-muted/30"
                        >
                          {/* <td className="py-3 pr-4">
                            <div className="flex items-center gap-2">
                              {l.assetType === 'equipment' && '🖥️'}
                              {l.assetType === 'machinery' && '⚙️'}
                              {l.assetType === 'furniture' && '🪑'}
                              {l.assetType === 'vehicle' && '🚗'}
                              {l.assetType === 'building' && '🏢'}
                              {(!l.assetType || l.assetType === 'other') && '📦'}
                            </div>
                          </td> */}
                          <td className="py-3 pr-4 font-medium">
                            {l.item || "—"}
                          </td>
                          <td className="py-3 pr-4">{l.description}</td>
                          <td className="py-3 pr-4">{l.hsn || "—"}</td>
                          <td className="py-3 pr-4">
                            {formatCurrency(getPoLineUnitPrice(l))}
                          </td>
                          <td className="py-3 pr-4">{l.qty}</td>
                          <td className="py-3 pr-4">
                            {isInterState ? (
                              <span>{igst}% IGST</span>
                            ) : (
                              <span>
                                {cgst}% + {sgst > 0 ? sgst : utgst > 0 ? utgst : 0}%
                              </span>
                            )}
                          </td>
                          <td className="py-3 pr-0 text-right font-medium">
                            {formatCurrency(total)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="border-t">
                    <tr>
                      <td
                        colSpan={6}
                        className="py-3 pr-4 text-right text-muted-foreground"
                      >
                        Subtotal
                      </td>
                      <td className="py-3 pr-0 text-right font-medium">
                        {formatCurrency(totals.subTotal)}
                      </td>
                    </tr>

                    {/* Enhanced GST Summary based on actual tax structure */}
                    {(() => {
                      const lines = assetLines;

                      const buildMap = (
                        key: "cgst" | "sgst" | "igst" | "utgst"
                      ) =>
                        lines.reduce((map: Record<number, number>, l: any) => {
                          const pct = Number(l[key] || 0);
                          if (pct <= 0) return map;
                          const amt =
                            (getPoLineUnitPrice(l) * Number(l.qty || 0) * pct) / 100;
                          map[pct] = (map[pct] || 0) + amt;
                          return map;
                        }, {} as Record<number, number>);

                      const igstMap = buildMap("igst");
                      const cgstMap = buildMap("cgst");
                      const sgstMap = buildMap("sgst");
                      const utgstMap = buildMap("utgst");

                      // IGST present -> list by percentage
                      if (Object.keys(igstMap).length > 0) {
                        return Object.keys(igstMap)
                          .map(Number)
                          .sort((a, b) => a - b)
                          .map((k) => (
                            <tr key={`igst_${k}`}>
                              <td
                                colSpan={6}
                                className="py-3 pr-4 text-right text-muted-foreground"
                              >
                                IGST ({k}%):
                              </td>
                              <td className="py-3 pr-0 text-right font-medium">
                                {formatCurrency(igstMap[k])}
                              </td>
                            </tr>
                          ));
                      }

                      // If UTGST used, handle CGST/UTGST pairs (group by pair to preserve association)
                      if (Object.keys(utgstMap).length > 0) {
                        // build pair map same as earlier (cg_ut => total amt)
                        const pairMap = lines.reduce(
                          (map: Record<string, number>, l: any) => {
                            const cg = Number(l.cgst || 0);
                            const ut = Number(l.utgst || 0);
                            if (cg <= 0 && ut <= 0) return map;
                            const key = `${cg}_${ut}`;
                            const amt =
                              (getPoLineUnitPrice(l) *
                                Number(l.qty || 0) *
                                (cg + ut)) /
                              100;
                            map[key] = (map[key] || 0) + amt;
                            return map;
                          },
                          {} as Record<string, number>
                        );

                        return Object.entries(pairMap).map(([pair, amt]: any) => {
                          const [cgStr, utStr] = pair.split("_") as [string, string];
                          const cg = Number(cgStr);
                          const ut = Number(utStr);

                          if (cg === ut && cg > 0) {
                            return (
                              <tr key={`cgut_${pair}`}>
                                <td
                                  colSpan={6}
                                  className="py-3 pr-4 text-right text-muted-foreground"
                                >
                                  CGST/UTGST ({cg}%):
                                </td>
                                <td className="py-3 pr-0 text-right font-medium">
                                  {formatCurrency(Number(amt))}
                                </td>
                              </tr>
                            );
                          }

                          // show separate lines for different percentages (CGST then UTGST)
                          return (
                            <React.Fragment key={`cgut_${pair}`}>
                              <tr>
                                <td
                                  colSpan={6}
                                  className="py-3 pr-4 text-right text-muted-foreground"
                                >
                                  CGST ({cg}%):
                                </td>
                                <td className="py-3 pr-0 text-right font-medium">
                                  {formatCurrency(
                                    (Number(amt) * (cg || 0)) /
                                    ((cg || 0) + (ut || 0)) || 0
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td
                                  colSpan={6}
                                  className="py-3 pr-4 text-right text-muted-foreground"
                                >
                                  UTGST ({ut}%):
                                </td>
                                <td className="py-3 pr-0 text-right font-medium">
                                  {formatCurrency(
                                    (Number(amt) * (ut || 0)) /
                                    ((cg || 0) + (ut || 0)) || 0
                                  )}
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        });
                      }

                      // Default: CGST + SGST handling.
                      // Build union of percentages and render combined line if both exist for same percentage,
                      // otherwise render each tax line separately.
                      const allKeys = Array.from(
                        new Set([
                          ...Object.keys(cgstMap).map(Number),
                          ...Object.keys(sgstMap).map(Number),
                        ])
                      ).sort((a, b) => a - b);

                      return allKeys.map((k) => {
                        const hasCg = !!cgstMap[k];
                        const hasSg = !!sgstMap[k];

                        if (hasCg && hasSg) {
                          return (
                            <tr key={`cgsg_${k}`}>
                              <td
                                colSpan={6}
                                className="py-3 pr-4 text-right text-muted-foreground"
                              >
                                CGST/SGST ({k}%):
                              </td>
                              <td className="py-3 pr-0 text-right font-medium">
                                {formatCurrency(
                                  (cgstMap[k] || 0) + (sgstMap[k] || 0)
                                )}
                              </td>
                            </tr>
                          );
                        }

                        if (hasCg) {
                          return (
                            <tr key={`cg_${k}`}>
                              <td
                                colSpan={6}
                                className="py-3 pr-4 text-right text-muted-foreground"
                              >
                                CGST ({k}%):
                              </td>
                              <td className="py-3 pr-0 text-right font-medium">
                                {formatCurrency(cgstMap[k])}
                              </td>
                            </tr>
                          );
                        }

                        // only SGST present
                        return (
                          <tr key={`sg_${k}`}>
                            <td
                              colSpan={6}
                              className="py-3 pr-4 text-right text-muted-foreground"
                            >
                              SGST ({k}%):
                            </td>
                            <td className="py-3 pr-0 text-right font-medium">
                              {formatCurrency(sgstMap[k])}
                            </td>
                          </tr>
                        );
                      });
                    })()}

                    <tr className="border-t">
                      <td
                        colSpan={6}
                        className="py-3 pr-4 text-right text-lg font-semibold"
                      >
                        Total:
                      </td>
                      <td className="py-3 pr-0 text-right text-lg font-semibold text-[hsl(var(--color-admin-secondary))]">
                        {formatCurrency(totals.total)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </CardContent>
            </Card>

            {/* Supplier & Customer Details */}
            <Card className="flex flex-col max-h-[calc(100vh-240px+1cm)] min-h-0">
              <CardHeader className="shrink-0">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Supplier & Customer
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 min-h-0 overflow-y-auto space-y-4">
                <div className="space-y-3">
                  <div className="font-medium text-[hsl(var(--color-admin-secondary))] flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Supplier
                  </div>
                  <div className="grid gap-2 text-sm">
                    <div className="flex items-start gap-2">
                      <Users className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div className="flex-1">
                        <span className="text-muted-foreground">Name: </span>
                        <span className="font-medium">
                          {supplier?.company_name}
                        </span>
                      </div>
                    </div>
                    {supplierSpoc?.email && (
                      <div className="flex items-start gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div className="flex-1">
                          <span className="text-muted-foreground">Email: </span>
                          <span>{supplierSpoc.email}</span>
                        </div>
                      </div>
                    )}
                    {po?.supplierGST && (
                      <div className="flex items-start gap-2">
                        <Receipt className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div className="flex-1">
                          <span className="text-muted-foreground">GSTIN: </span>
                          <span>{po?.supplierGST}</span>
                        </div>
                      </div>
                    )}
                    {po.supplierAddress && (
                      <div className="flex items-start gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div className="flex-1">
                          <span className="text-muted-foreground">
                            Address:{" "}
                          </span>
                          <span>{po.supplierAddress}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="font-medium text-[var(--color-primary-dark)] flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Delivery Address
                  </div>
                  <div className="text-sm text-muted-foreground pl-6">
                    {po.deliveryAddress || "Same as supplier address"}
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="font-medium flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Customer
                  </div>
                  <div className="text-sm font-medium pl-6">
                    {customer?.entity_name}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="font-medium flex items-center gap-2 text-muted-foreground">
                      <ClipboardList className="h-4 w-4" />
                      Requirement
                    </div>
                    <div className="text-sm font-medium pl-6">
                      {po?.requirementId ?? "—"}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="font-medium flex items-center gap-2 text-muted-foreground">
                      <Layers className="h-4 w-4" />
                      Product Type
                    </div>
                    <div className="text-sm font-medium pl-6">
                      {po?.productType ?? "—"}
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    PO Attachments
                  </div>

                  {/* ********* */}
                  {attachmentUrl != "" ? (
                    <div
                      key={attachmentUrl}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-[hsl(var(--color-admin-primary))]" />
                        <span className="text-sm font-medium">
                          PO Attachment
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewAttachment(attachmentUrl)}
                          className="h-8 px-3 text-xs"
                        >
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleRemoveAttachment("attachmentUrl")
                          }
                          className="h-8 px-3 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-sm text-muted-foreground">
                        No attachments
                      </div>

                      {/* Generic upload block (dashed) */}
                      <div className="flex items-center justify-between p-3 border-2 border-dashed rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex items-center gap-2">
                          <Upload className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            Upload PO Attachment
                          </span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!!uploadingDocType}
                          onClick={() => handleUploadForType("attachmentUrl")}
                          className="h-8 px-3 text-xs"
                        >
                          {uploadingDocType ? "Uploading..." : "Upload"}
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Timeline
                  </div>
                  <div className="grid gap-2 text-sm pl-6">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Created At:</span>
                      <span className="font-medium">
                        {new Date(po.createdAt).toLocaleDateString("en-IN")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Updated At:</span>
                      <span className="font-medium">
                        {new Date(
                          po.updatedAt || po.createdAt
                        ).toLocaleDateString("en-IN")}
                      </span>
                    </div>
                    {(currentStatus === "approved" ||
                      currentStatus === "delivered") && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">
                            Approved At:
                          </span>
                          <span className="font-medium">
                            {new Date(
                              po.updatedAt || po.createdAt
                            ).toLocaleDateString("en-IN")}
                          </span>
                        </div>
                      )}
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Created By:</span>
                      <span className="font-medium">
                        {
                          adminUserList.find(
                            (admin) => String(po.createdBy) === String(admin.id)
                          )?.name
                        }
                      </span>
                    </div>
                    {(currentStatus === "approved" ||
                      currentStatus === "delivered") && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">
                            Approved By:
                          </span>
                          <span className="font-medium">
                            {
                              adminUserList.find(
                                (admin) =>
                                  String(po.approvedBy) === String(admin.id)
                              )?.name
                            }
                          </span>
                        </div>
                      )}
                  </div>
                </div>


              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Proforma Invoice
              </CardTitle>
              {currentStatus === "approved" && (
                <AddInvoice po={po} addinvoice={handleRefresh} />
              )}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice Number</TableHead>
                    <TableHead>Supplier Name</TableHead>
                    <TableHead>Asset Details</TableHead>
                    <TableHead>Issued At</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Invoice Amount</TableHead>
                    <TableHead>Paid Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invs.map((inv) => {
                    // const outstanding = invoiceOutstanding(inv, store.payments);
                    const paidPayment = paidPayments.filter(
                      (p) => p.invoiceId === inv.id
                    );
                    const paidAmount = paidPayment.reduce(
                      (sum, p) => sum + p.amount,
                      0
                    );
                    const assetLines =
                      assetTracking.filter(
                        (line) => line.invoice_id === inv.id
                      ) || [];

                    return (
                      <TableRow key={inv.id} className="hover:bg-muted/30">
                        <TableCell className="font-medium font-mono">
                          {inv.number}
                        </TableCell>
                        <TableCell>
                          {supplier?.company_name || supplier?.name || "—"}
                        </TableCell>
                        <TableCell>
                          <AssetDetails assets={assetLines} />
                        </TableCell>
                        <TableCell>
                          {new Date(inv.date).toLocaleDateString("en-IN")}
                        </TableCell>
                        <TableCell>
                          {inv.due_date
                            ? new Date(inv.due_date).toLocaleDateString("en-IN")
                            : "—"}
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrencyINR(inv.amount + inv.tax)}
                        </TableCell>
                        <TableCell className="font-medium text-green-600">
                          {formatCurrencyINR(paidAmount)}
                        </TableCell>
                        <TableCell>
                          <POStatusBadge status={inv.status as any} />
                        </TableCell>
                        <TableCell className="text-right">
                          <InvoiceDetailButton
                            invoice={inv}
                            po={po}
                            addPayment={handleRefresh}
                            adminUserList={adminUserList}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Asset Tracking Tab */}
        <TabsContent value="tracking">
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Individual Asset Tracking
                </CardTitle>
                <div className="text-sm text-muted-foreground">
                  Assets are tracked individually only after invoicing
                </div>
              </div>
              {/* <AssetBulkUpdateDialog
                assets={store.individualAssets
                  .filter((asset) => asset.poId === po.id && asset.invoiceId)
                  .map(
                    (asset) =>
                    ({
                      ...asset,
                      qty: 1,
                      description: asset.description || "",
                      unitPrice: asset.unitPrice || 0,
                    } as any)
                  )}
                onUpdate={(updates) => {
                  Object.entries(updates).forEach(([assetId, patch]) => {
                    store.updateIndividualAsset(assetId, patch);
                  });
                }}
              /> */}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Asset ID</TableHead>
                    <TableHead>Supplier Name</TableHead>
                    <TableHead>Asset Name</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Total Price</TableHead>
                    <TableHead>Insurance End Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assetTracking
                    .filter((asset) => asset.po_id === po.id && asset.invoice_id)
                    .map((asset) => {
                      const assetLine: any = po.lines.find(
                        (line: any) => line.id === asset.asset_line_id
                      );
                      return (
                        <TableRow key={asset.id} className="hover:bg-muted/30">
                          <TableCell>
                            <div className="flex items-center gap-3">

                              {/* LEFT SIDE → Asset Image */}
                              <div className="h-12 w-12 flex-shrink-0">
                                {asset.primaryImageUrl ? (
                                  <img
                                    src={asset.primaryImageUrl}
                                    alt="Asset"
                                    className="h-12 w-12 rounded-md object-cover border"
                                  />
                                ) : (
                                  <AssetTypeIcon
                                    assetType={asset.asset_type || "other"}
                                    className="h-12 w-12 text-[hsl(var(--color-admin-primary))]"
                                  />
                                )}
                              </div>

                              {/* RIGHT SIDE → Asset ID + Serial No */}
                              <div>
                                <div className="font-medium font-mono text-[hsl(var(--color-admin-primary))]">
                                  {asset.unique_asset_id}
                                </div>

                                {asset.serial_number && (
                                  <div className="text-xs text-muted-foreground">
                                    SN: {asset.serial_number}
                                  </div>
                                )}
                              </div>

                            </div>
                          </TableCell>

                          <TableCell>
                            {supplier?.company_name || supplier?.name || "—"}
                          </TableCell>

                          <TableCell>
                            <div className="font-medium">
                              {assetLine?.item}
                            </div>
                            {asset.specification && (
                              <div className="text-xs text-muted-foreground">
                                {asset.specification}
                              </div>
                            )}
                          </TableCell>

                          <TableCell>
                            {asset.qty || "1"}
                          </TableCell>

                          <TableCell className="font-medium">
                            {asset
                              ? (formatCurrency((asset.unit_price + (asset.unit_price * asset.tax_rate / 100)) * asset.qty))
                              : "—"}
                          </TableCell>

                          <TableCell>
                            {asset.insurance_end_date ? (
                              <span
                                className={
                                  new Date(asset.insurance_end_date) < new Date()
                                    ? "text-destructive"
                                    : ""
                                }
                              >
                                {new Date(
                                  asset.insurance_end_date
                                ).toLocaleDateString("en-IN")}
                              </span>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell>
                            <AssetStatusBadge status={asset.status} />
                            {/* <TableCell><POStatusBadge status={inv.status as any} /></TableCell> */}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                          
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleAssetViewDetails(asset)}
                                className="h-6 w-6 p-0"
                              >
                                {/* <Eye className="h-4 w-4 mr-1" /> */}
                                <Eye className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <POPreviewModal
        po={{
          ...po,
          supplier: supplier,
        }}
        currentStatus={currentStatus}
        customer={customer}
        isOpen={poPreviewOpen}
        onClose={() => setPoPreviewOpen(false)}
      />

      {selectedAsset && (
        <AssetDetailSidebar
          open={assetOpen}
          onOpenChange={setAssetOpen}
          asset={selectedAsset}
          po={po}
          supplier={supplier}
          customer={customer}
          isIndividualAsset={true}
          assetsCategories={assetsCategories}
          invoices={invs}
          updateDetails={handleUpdateAssetDetails}
          onPrimaryImageChange={handlePrimaryImageChange}
          onInsurancePolicySaved={fetchAssetsTracking}
        />
      )}
    </>
  );
}

function ConfirmApprove({ onConfirm }: { onConfirm: () => void }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="secondary"
          className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))] text-white"
        >
          Approve
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm Underwriter Approval</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Approving will lock the PO for editing.
        </p>
        <DialogFooter>
          <Button
            onClick={onConfirm}
            className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))] text-white"
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddInvoice({ po, addinvoice }: { po: any; addinvoice: () => void }) {
  const { refreshWorkflow } = useCustomerWorkflow();
  const poId = po.id;
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState("");
  const [date, setDate] = useState<string>(
    new Date().toISOString().slice(0, 10)
  );
  const [dueDate, setDueDate] = useState<string>("");
  // const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [availableAssets, setAvailableAssets] = useState<AvailableAssets[]>([]);

  const [isAddingInvoice, setIsAddingInvoice] = useState(false);
  // Quantity-based selection per asset line
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  // Bulk toggle per asset line
  const [bulkToggles, setBulkToggles] = useState<Record<string, boolean>>({});


  useEffect(() => {
    fetchIndividualAssets();
  }, []);

  const fetchIndividualAssets = async () => {
    const response = await apiService.get<any>(
      `/purchase-order/get-individual-assets/${poId}`
    );
    setAvailableAssets(response.data);
  };

  // Get asset lines for display
  // const assetLinesMap = new Map(po.lines.map((line: any) => [line.id, line]));




  const { totalBase, totalTax, selectedAssetIds } = useMemo(() => {
    let base = 0;
    let tax = 0;
    const assetIds: string[] = [];

    Object.entries(quantities).forEach(([lineId, qty]) => {
      if (qty <= 0) return;

      const data = availableAssets.find((asset) => String(asset.id) === String(lineId));
      if (!data) return;

      // const { line } = data;
      const cgst = data?.cgst || 0;
      const sgst = data?.sgst || 0;
      const igst = data?.igst || 0;
      const utgst = data?.utgst || 0;
      const totalTaxRate = cgst + sgst + igst + utgst;

      // Take first N assets
      // const selectedAssets = data?.slice(0, qty);
      // selectedAssets.forEach((asset:any) => assetIds.push(String(asset.id)));

      base += data.unit_price * qty;
      tax += (data.unit_price * qty * totalTaxRate) / 100;


    });

    return { totalBase: base, totalTax: tax, selectedAssetIds: assetIds };
  }, [quantities, availableAssets, bulkToggles]);


  const selectedAssets = availableAssets.filter((asset) =>
    selectedAssetIds.includes(String(asset.id))
  );

  const add = async () => {
    if (!number || isAddingInvoice) return;
    setIsAddingInvoice(true);
    let attachmentUrl: string | undefined = undefined;

    if (attachment) {
      const fd = new FormData();
      fd.append("files", attachment);
      fd.append("f", String(poId));
      fd.append("folder", po.customerId + "/po/invoice/attachment");
      try {
        const uploadRes: any = await apiService.post(
          "/purchase-order/doc-upload",
          fd,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        const urls = uploadRes.data?.fileKeys ?? uploadRes.data ?? [];
        // take first url (adjust if backend returns single string)
        attachmentUrl = urls[0];
      } catch (err) {
        toast.error("Failed to upload attachment");
        console.error("upload error", err);
        setIsAddingInvoice(false);
        return;
      }
    }

    const invoice = {
      poId,
      number,
      date,
      dueDate,
      amount: totalBase,
      tax: totalTax,
      assetLineIds: [
        ...new Set(selectedAssets.map((asset) => String(asset.id))),
      ], // Unique asset line IDs
      attachmentUrl,
    };

    try {
      const response: any = await apiService.post(
        "/purchase-order/create-invoice",
        { invoice, quantities, bulkToggles }
      );
      toast.success("Invoice Created Successfully");
      // refresh available assets / individual assets
      await addinvoice();
      await fetchIndividualAssets();
      // reset form
      setNumber("");
      // setSelectedAssetIds([]);
      setAttachment(null);
      setQuantities({});
      setBulkToggles({});
      setOpen(false);
    } catch (err) {
      toast.error("Failed to Create Invoice");
      console.error("create invoice error", err);
    } finally {
      setIsAddingInvoice(false);
      await refreshWorkflow();
    }
  };

  const isWholeNumberQty = (n: number) =>
    Number.isFinite(n) && n > 0 && Number.isInteger(Number(n));

  const syncBulkForQty = (lineId: string, nextValue: number, previousQty: number) => {
    if (nextValue > 1 && previousQty <= 1) {
      setBulkToggles((prev) => ({ ...prev, [lineId]: true }));
    } else if (nextValue <= 1) {
      setBulkToggles((prev) => ({ ...prev, [lineId]: false }));
    }
  };

  const handleAssetSelect = useCallback(
    (lineId: string, checked: boolean) => {
      const maxQty =
        Number(
          availableAssets.find((asset) => String(asset.id) === String(lineId))
            ?.remaining_qty
        ) || 0;
      const previousQty = quantities[lineId] || 0;
      const nextValue = checked ? maxQty : 0;

      setQuantities((prev) => ({ ...prev, [lineId]: nextValue }));
      syncBulkForQty(lineId, nextValue, previousQty);
    },
    [availableAssets, quantities]
  );

  const handleQuantityChange = useCallback(
    (lineId: string, value: number) => {
      const maxQty =
        Number(
          availableAssets.find((asset) => String(asset.id) === String(lineId))
            ?.remaining_qty
        ) || 0;

      // Decimal available qty is locked to full amount when selected — no manual edit
      if (!isWholeNumberQty(maxQty)) {
        return;
      }

      // Whole-number available: only allow integers 0..max
      const intValue = Math.floor(Number(value) || 0);
      const nextValue = Math.max(0, Math.min(intValue, maxQty));
      const previousQty = quantities[lineId] || 0;

      setQuantities((prev) => ({ ...prev, [lineId]: nextValue }));
      syncBulkForQty(lineId, nextValue, previousQty);
    },
    [availableAssets, quantities]
  );

  const handleBulkToggle = useCallback((lineId: string, checked: boolean) => {
    setBulkToggles((prev) => ({ ...prev, [lineId]: checked }));
  }, []);

  const selectAllQuantities = () => {
    const newQuantities: Record<string, number> = {};
    const newBulkToggles: Record<string, boolean> = {};
    availableAssets.forEach((asset) => {
      const id = String(asset.id);
      const remaining = Number(asset.remaining_qty) || 0;
      newQuantities[id] = remaining;
      newBulkToggles[id] = remaining > 1;
    });
    setQuantities(newQuantities);
    setBulkToggles(newBulkToggles);
  };

  const clearAllQuantities = () => {
    setQuantities({});
    setBulkToggles({});
  };


  const totalSelected = Object.values(quantities).reduce((sum, qty) => sum + (qty || 0), 0);
  const totalAvailable = availableAssets.reduce((sum, asset) => sum + asset.remaining_qty, 0);
  const formatQtyDisplay = (n: number) => {
    const rounded = Math.round((Number(n) || 0) * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
  };
  const selectedItemCount = availableAssets.filter(
    (asset) => (quantities[String(asset.id)] || 0) > 0
  ).length;
  const totalItemCount = availableAssets.length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="secondary"
          className="bg-[hsl(var(--color-admin-primary))] hover:bg-[hsl(var(--color-admin-primary))/0.9] text-black"
        >
          Add Invoice
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Invoice</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Invoice Number</Label>
              <Input
                className="mt-1"
                placeholder="Invoice #"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input
                className="mt-1"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                className="mt-1"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Upload Invoice (PDF)</Label>
              <Input
                className="mt-1"
                type="file"
                accept="application/pdf"
                onChange={(e) => setAttachment(e.target.files?.[0] || null)}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Select Assets</Label>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAllQuantities} disabled={totalAvailable === 0}>
                  Select All
                </Button>
                <Button variant="outline" size="sm" onClick={clearAllQuantities} disabled={totalSelected === 0}>
                  Clear All
                </Button>
              </div>
            </div>
            <div className="mt-2 max-h-64 border rounded-md">
              {availableAssets.length === 0 ? (
                <div className="text-sm text-muted-foreground p-4 text-center">
                  No assets available for invoicing. All assets may already be
                  invoiced.
                </div>
              ) : (
                <>
                  {/* <div className="sticky top-0 bg-background border-b px-3 py-2 text-xs text-muted-foreground font-medium">
                    {selectedAssetIds.length} of {availableAssets.length} assets selected
                  </div> */}

                  <div className="sticky top-0 bg-background border-b px-3 py-2 text-xs text-muted-foreground font-medium z-10 flex items-center justify-between gap-3">
                    <span>
                      {formatQtyDisplay(totalSelected)} of {formatQtyDisplay(totalAvailable)} qty selected
                    </span>
                    <span className="shrink-0">
                      {selectedItemCount} of {totalItemCount} items selected
                    </span>
                  </div>

                  {/*   <div className="p-2 space-y-1"> */}

                  <div className="p-3 space-y-3 max-h-55 overflow-y-auto bg-white">
                    {availableAssets.map((asset) => {
                      const available = Number(asset.remaining_qty) || 0;
                      const line = asset;
                      const lineId = String(asset.id);
                      const qty = quantities[lineId] || 0;
                      const isSelected = qty > 0;
                      const canEditQty = isSelected && isWholeNumberQty(available);
                      const isBulk = bulkToggles[lineId] || false;
                      const cgst = line?.cgst || 0;
                      const sgst = line?.sgst || 0;
                      const igst = line?.igst || 0;
                      const utgst = line?.utgst || 0;
                      const totalTaxRate = cgst + sgst + igst + utgst;

                      return (
                        <div
                          key={asset.id}
                          className={`rounded-lg border p-4 space-y-3 transition-colors ${isSelected ? 'bg-primary/5 border-primary/20' : 'border-border'
                            }`}
                        >
                          {/* Header Row */}
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex flex-1 min-w-0 items-start gap-3">
                              <Checkbox
                                id={`select-asset-${lineId}`}
                                checked={isSelected}
                                onCheckedChange={(checked) =>
                                  handleAssetSelect(lineId, checked === true)
                                }
                                className="mt-1"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <label
                                    htmlFor={`select-asset-${lineId}`}
                                    className="font-semibold whitespace-normal break-words cursor-pointer"
                                  >
                                    {line.item}
                                  </label>
                                  {isBulk && isSelected && (
                                    <Badge variant="secondary" className="text-xs">Bulk</Badge>
                                  )}
                                </div>
                                {!isWholeNumberQty(available) && available > 0 && (
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    Decimal qty — select to take full available ({available})
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Quantity Input */}
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-sm text-muted-foreground">Qty:</span>
                              <Input
                                type="number"
                                min={0}
                                max={available}
                                step={canEditQty ? 1 : "any"}
                                value={qty || ""}
                                disabled={!isSelected}
                                readOnly={!canEditQty}
                                onChange={(e) => {
                                  if (!canEditQty) return;
                                  const parsed = parseInt(e.target.value, 10);
                                  handleQuantityChange(
                                    lineId,
                                    Number.isFinite(parsed) ? parsed : 0
                                  );
                                }}
                                className={`w-24 h-8 text-center ${!canEditQty ? "bg-muted/40" : ""}`}
                                placeholder="0"
                                title={
                                  !isSelected
                                    ? "Check Select to fill available qty"
                                    : canEditQty
                                      ? "Enter whole number qty (1, 2, 3…)"
                                      : "Decimal available qty cannot be changed"
                                }
                              />
                              <span className="text-sm text-muted-foreground">/ {available}</span>
                            </div>
                          </div>

                          {/* Price Info */}
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div className="rounded-md border p-2 bg-card/50">
                              <div className="text-muted-foreground">Unit Price</div>
                              <div className="font-medium">{formatCurrency(line.unit_price)}</div>
                            </div>
                            <div className="rounded-md border p-2 bg-card/50">
                              <div className="text-muted-foreground">GST ({totalTaxRate}%)</div>
                              <div className="font-medium">{formatCurrency(line.unit_price * totalTaxRate / 100)}</div>
                            </div>
                            <div className="rounded-md border p-2 bg-card/50">
                              <div className="text-muted-foreground">Total (×{qty || 0})</div>
                              <div className="font-semibold text-[hsl(var(--admin-secondary))]">
                                {formatCurrency((line.unit_price + line.unit_price * totalTaxRate / 100) * (qty || 0))}
                              </div>
                            </div>
                          </div>

                          {/* Bulk only when selected qty > 1 */}
                          {qty > 1 && (
                            <div className="flex items-center gap-2 pt-2 border-t">
                              <Switch
                                checked={isBulk}
                                onCheckedChange={(checked: any) => handleBulkToggle(lineId, checked)}
                                id={`bulk-${lineId}`}
                              />
                              <label
                                htmlFor={`bulk-${lineId}`}
                                className="text-sm text-muted-foreground cursor-pointer"
                              >
                                Mark as Bulk (for high-quantity items like furniture)
                              </label>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>


                  {/* </div> */}
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 p-3 bg-muted/30 rounded">
            <div>
              <div className="text-xs text-muted-foreground">Base Amount</div>
              <div className="font-medium">{formatCurrency(totalBase)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Tax</div>
              <div className="font-medium">{formatCurrency(totalTax)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Total</div>
              <div className="font-semibold text-[var(--color-primary-dark)]">
                {formatCurrency(totalBase + totalTax)}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={add}
            disabled={!number || isAddingInvoice}
            className="cursor-pointer text-white"
          >
            {isAddingInvoice ? "Creating Invoice..." : "Add Invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssetStatusBadge({ status }: { status?: string }) {
  const getStatusColor = (status?: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300";
      case "in_process":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300";
      case "shipped":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300";
      case "delivered":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300";
    }
  };

  return (
    <Badge className={`${getStatusColor(status)} border-0 text-xs`}>
      {status ? status.replace("_", " ").toUpperCase() : "PENDING"}
    </Badge>
  );
}

function AssetDetails({ assets }: { assets: any[] }) {
  if (!assets || assets.length === 0) return <span>No assets</span>;

  // Group assets by assetLineId and sum qty (fall back to count=1 if qty not provided)
  const groupsMap: Record<
    string,
    { description?: string; qty: number; items: any[] }
  > = assets.reduce((acc, a) => {
    const key = String(a.assetLineId ?? a.lineId ?? a.id ?? "unknown");
    if (!acc[key]) {
      acc[key] = {
        description: a.item || "",
        qty: 0,
        items: [],
      };
    }
    const qtyToAdd = Number(a.qty ?? 1); // if qty missing, count as 1
    acc[key].qty += isNaN(qtyToAdd) ? 1 : qtyToAdd;
    acc[key].items.push(a);
    return acc;
  }, {} as Record<string, { description?: string; qty: number; items: any[] }>);

  const groups = Object.values(groupsMap);

  // If single group, show description + qty
  if (groups.length === 1) {
    const g = groups[0];
    const desc = g.description || g.items[0]?.description || "Item";
    return <span className="text-sm">{`${desc} — Qty: ${g.qty}`}</span>;
  }

  // Multiple groups: show first description summary + tooltip listing grouped quantities
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-sm cursor-pointer underline decoration-dotted">
            {groups[0].description || groups[0].items[0]?.description || "Item"}{" "}
            +{groups.length - 1} more
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs bg-white">
          <div className="space-y-1">
            {groups.map((g, idx) => (
              <div key={idx} className="text-xs">
                {g.description || g.items[0]?.description || `Item ${idx + 1}`}{" "}
                - Qty: {g.qty}
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function getPoLineUnitPrice(l: { unitPrice?: number; unit_price?: number }): number {
  return Number(l.unitPrice ?? l.unit_price ?? 0);
}

function getPoLineAmounts(l: {
  unitPrice?: number;
  unit_price?: number;
  qty?: number;
  cgst?: number;
  sgst?: number;
  igst?: number;
  utgst?: number;
}) {
  const unitPrice = getPoLineUnitPrice(l);
  const qty = Number(l.qty || 0);
  const base = unitPrice * qty;
  const cgst = Number(l.cgst || 0);
  const sgst = Number(l.sgst || 0);
  const igst = Number(l.igst || 0);
  const utgst = Number(l.utgst || 0);
  const totalTaxRate = cgst + sgst + igst + utgst;
  const taxAmount = (base * totalTaxRate) / 100;
  return {
    cgst,
    sgst,
    igst,
    utgst,
    base,
    taxAmount,
    total: base + taxAmount,
  };
}

function computePODetailTotals(lines: Parameters<typeof getPoLineAmounts>[0][]) {
  let subTotal = 0;
  let tax = 0;
  for (const l of lines || []) {
    const { base, taxAmount } = getPoLineAmounts(l);
    subTotal += base;
    tax += taxAmount;
  }
  return {
    subTotal,
    tax,
    total: subTotal + tax,
  };
}

function formatCurrency(amount: number): string {
  const value = Number(amount) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}
